import React from 'react'
import AdminSidebar from '@components/admin/AdminSidebar'
import { useNavigate, useParams } from 'react-router-dom'
import 'twin.macro'
import { api } from '@api/index'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { FieldValues, useForm } from 'react-hook-form'
import useModal from '@hooks/useModal'
import MessageModal from '@components/shared/MessageModal'
import { Message } from '@utils/message'
import StBaseSelect from '@components/shared/StBaseSelect'
import { Constant } from '@utils/constant'
import { useAuth } from '@hooks/useAuth'

const AdminUserDetailManagement = () => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { id: userUUID } = useParams()
  const [openResModal] = useModal()

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<any>({
    mode: 'onChange',
  })

  const {
    isLoading,
    error,
    data: userInfo,
  } = useQuery(
    ['UserDetail'],
    () => api.adminService.getUserDetail(Number(userUUID)),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        const currentAuth = data.authorities[data.authorities.length - 1]
        if (currentAuth) {
          setValue('authorities', currentAuth)
        }
      },
    }
  )

  const deleteUserMutation = useMutation((userUUID: number) =>
    api.adminService.deleteUser(Number(userUUID))
  )

  const changeAuthoritiesMutation = useMutation(
    (authorities: Authorities) =>
      api.adminService.changeAuthorities(Number(userUUID), { authorities }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['UserDetail'])
      },
    }
  )

  const handleDeleteUserClick = async () => {
    const res = await openResModal(
      <MessageModal mode="req">{Message.question.DELETE_USER}</MessageModal>
    )
    if (res) {
      deleteUserMutation.mutate(Number(userUUID), {
        onSuccess: async () => {
          await openResModal(
            <MessageModal>{Message.info.SUCCESS_DELETE_USER}</MessageModal>
          )
          navigate('/admin/user-management')
        },
      })
    }
  }

  const handleSaveClick = (form: FieldValues) => {
    const authoritiesSet = new Set([Constant.role.ROLE_USER_HASH])
    authoritiesSet.add(form.authorities)
    changeAuthoritiesMutation.mutate([...authoritiesSet] as Authorities, {
      onSuccess: () => {
        openResModal(
          <MessageModal>{Message.info.SUCCESS_CHANGE_AUTHORITIES}</MessageModal>
        )
      },
    })
  }

  const handleBackClick = () => {
    navigate(-1)
  }

  return (
    <>
      <section tw="w-1050 flex gap-32">
        <AdminSidebar />
        <main tw="w-full">
          <section tw="w-full">
            <form
              onSubmit={handleSubmit(handleSaveClick)}
              id="user-modify-form"
            >
              <h1 tw="text-2xl border-b-2 font-bold pb-10 border-neutral-400">
                유저상세정보 관리
              </h1>
              <div tw="py-15">
                <div tw="w-full bg-neutral-100 rounded-lg p-20">
                  <div tw="w-full flex justify-between mb-15 items-center">
                    <h2 tw="font-bold text-xl flex items-center">유저정보</h2>
                    {user?.id !== Number(userUUID) && (
                      <button
                        onClick={handleDeleteUserClick}
                        type="button"
                        className="button"
                        tw="bg-slate-200 px-20"
                      >
                        삭제
                      </button>
                    )}
                  </div>
                  <div tw="flex flex-wrap gap-x-16 gap-y-20">
                    <div tw="flex flex-col">
                      <div tw="min-w-250 flex gap-12 items-center justify-between">
                        <div>닉네임</div>
                        <div tw="w-170 text-base">{userInfo?.nickname}</div>
                      </div>
                    </div>
                    <div tw="flex flex-col">
                      <div tw="min-w-250 flex gap-12 items-center justify-between">
                        <div>아이디</div>
                        <div tw="w-170 text-base">{userInfo?.username}</div>
                      </div>
                    </div>
                    <div tw="flex flex-col">
                      <div tw="min-w-250 flex gap-12 items-center justify-between">
                        <div>이메일</div>
                        <div tw="w-170 text-base">{userInfo?.email}</div>
                      </div>
                    </div>
                    <div tw="flex flex-col">
                      <div tw="min-w-250 flex gap-12 items-center justify-between">
                        <div>휴대폰</div>
                        <div tw="w-170 text-base">{userInfo?.phone}</div>
                      </div>
                    </div>
                    <div tw="flex flex-col">
                      <div tw="min-w-250 flex gap-12 items-center justify-between">
                        <div>성별</div>
                        <div tw="w-170 text-base">
                          {userInfo?.gender && Constant.gender[userInfo.gender]}
                        </div>
                      </div>
                    </div>
                    <div tw="flex flex-col">
                      <div tw="min-w-250 flex gap-12 items-center justify-between">
                        <div>생년월일</div>
                        <div tw="w-170 text-base">{userInfo?.birth}</div>
                      </div>
                    </div>
                    <div tw="min-w-250 flex gap-12 items-center justify-between">
                      <div>유저권한</div>
                      <div tw="w-170 text-base">
                        <StBaseSelect
                          disabled={user?.id === Number(userUUID)}
                          {...register('authorities')}
                          tw="w-150 px-10 py-0"
                          className="input"
                        >
                          <option value={Constant.role.ROLE_ADMIN_HASH}>
                            어드민
                          </option>
                          <option value={Constant.role.ROLE_USER_HASH}>
                            일반
                          </option>
                        </StBaseSelect>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </section>
          <section tw="w-full">
            <div tw="w-full bg-neutral-100 rounded-lg p-20">
              <h2 tw="font-bold text-xl  flex items-center">유저 접속 로그</h2>
            </div>
          </section>
          <div tw="w-full flex justify-end my-20 gap-12">
            <button
              onClick={handleBackClick}
              form="user-modify-form"
              type="button"
              tw="py-5 px-15"
              className="button"
            >
              뒤로가기
            </button>
            <button
              form="user-modify-form"
              type="submit"
              tw="py-5 px-15"
              className="button"
            >
              저장
            </button>
          </div>
        </main>
      </section>
    </>
  )
}

export default AdminUserDetailManagement
